import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Check, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

export function PaymentSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ state: "polling", tries: 0 });
    const tickRef = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            navigate("/dashboard", { replace: true });
            return;
        }
        let cancelled = false;
        const poll = async () => {
            tickRef.current += 1;
            try {
                const res = await api.paymentStatus(sessionId);
                if (cancelled) return;
                if (res.payment_status === "paid") {
                    setStatus({ state: "paid" });
                    return;
                }
                if (res.payment_status === "failed" || res.payment_status === "expired") {
                    setStatus({ state: "failed" });
                    return;
                }
                if (tickRef.current >= 20) {
                    setStatus({ state: "timeout" });
                    return;
                }
                setStatus((s) => ({ ...s, state: "polling", tries: tickRef.current }));
                setTimeout(poll, 2000);
            } catch (e) {
                if (!cancelled) setStatus({ state: "failed", message: e.message });
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [sessionId, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
            <div className="max-w-md w-full border border-foreground p-10">
                {status.state === "polling" && (
                    <>
                        <div className="overline mb-4">▍ Confirming payment</div>
                        <h1 className="text-3xl font-black tracking-tight">Talking to Stripe…</h1>
                        <div className="mt-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                            {status.tries || 1}s of polling
                        </div>
                    </>
                )}
                {status.state === "paid" && (
                    <>
                        <div className="overline mb-4 !text-accent">▍ Welcome to Pro</div>
                        <h1 className="text-4xl font-black tracking-tighter leading-[1]">
                            You&rsquo;re
                            <br />
                            <span className="text-accent">unlocked.</span>
                        </h1>
                        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                            Opus 4.7, unlimited domains, 500 MB storage, no badge on published sites. For the next 30 days.
                        </p>
                        <Link
                            to="/dashboard"
                            data-testid="post-payment-continue"
                            className="btn-brutal mt-8"
                        >
                            Back to the builder <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                        </Link>
                    </>
                )}
                {(status.state === "failed" || status.state === "timeout") && (
                    <>
                        <div className="overline mb-4 !text-accent">▍ Not confirmed</div>
                        <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
                            <XCircle className="w-6 h-6 text-accent" strokeWidth={1.5} /> Payment not confirmed
                        </h1>
                        <p className="mt-3 text-sm text-muted-foreground">
                            Either the payment failed or Stripe took too long to respond. If your card was charged, it will
                            reflect once the webhook lands.
                        </p>
                        <Link to="/dashboard" className="btn-outline-brutal mt-8">
                            Back to dashboard
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}

export function PaymentCancel() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
            <div className="max-w-md w-full border border-border p-10">
                <div className="overline mb-4">▍ Checkout cancelled</div>
                <h1 className="text-3xl font-black tracking-tight">No card charged.</h1>
                <p className="mt-3 text-sm text-muted-foreground">
                    Free plan is generous — you can keep building without upgrading. Come back any time.
                </p>
                <Link to="/dashboard" className="btn-brutal mt-8" data-testid="cancel-back">
                    Back to dashboard <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
            </div>
        </div>
    );
}
